/* eslint-disable react-refresh/only-export-components */
/* eslint-disable react-hooks-extra/no-direct-set-state-in-use-effect */
import type { ButtonProps } from '@connected/ui/components/button';
import type { LabelAsLabelProps } from '@connected/ui/components/label';
import type { RadioGroupProps as PrimitiveRadioGroupProps } from '@connected/ui/components/radio-group';
import type { ComponentPropsWithRef, ReactElement, ReactNode } from 'react';

import { Button } from '@connected/ui/components/button';
import { Label } from '@connected/ui/components/label';
import { Radio } from '@connected/ui/components/radio';
import { RadioGroup as PrimitiveRadioGroup } from '@connected/ui/components/radio-group';
import { cn, hexToRgba } from '@connected/ui/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { CheckIcon, ChevronDown } from 'lucide-react';
import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';

import type { OptionFieldContextState } from '../../hooks/use-option-field';
import type { Option, OptionExtra } from '../../lib/option';
import type { PrimativeFieldProps, PrimativeFormPropsOmit, PrimativeLabelType, PrimitiveValueType } from '../../lib/utils';

import { OptionFieldContext, useOptionField } from '../../hooks/use-option-field';
import { createValueMapsFromOptions } from '../../lib/option';
import { VALUE_MARKER } from '../../lib/utils';

export type RadioGroupOption<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined> = Option<Value, Extra>;
export type RadioGroupRenderItem<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined> = (props: RadioGroupItemProps<Value, Extra>) => ReactNode;
export type AnyRadioGroupRenderItem = RadioGroupRenderItem<any, any>;

export type RadioGroupContextState<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined, RenderItem extends RadioGroupRenderItem<Value, Extra> = typeof DefaultRadioGroupItem> = {
    name?: string;
    value: Value | undefined;
    onChange: (value: Value) => void;
    onBlur?: () => void;
    disabled?: boolean;
    selectedValue: Value | undefined;
    handleValueChange: (value: Value) => void;
    isOptionSelected: (option: Option<Value, Extra>) => boolean;
    valueToString: Map<Value, string>;
    RenderItem: RenderItem;
};

export const RadioGroupContext = createContext<RadioGroupContextState<PrimitiveValueType, OptionExtra | undefined> | null>(null);

export function useRadioGroupContext<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined, RenderItem extends RadioGroupRenderItem<Value, Extra> = typeof DefaultRadioGroupItem>() {
    const context = use(RadioGroupContext);

    if (!context)
        throw new Error('useRadioGroupContext must be used within a RadioGroup');

    return context as unknown as RadioGroupContextState<Value, Extra, RenderItem>;
}

export type RadioGroupItemProps<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined> = {
    option: RadioGroupOption<Value, Extra>;
    name?: string;
    value: Value;
    stringValue: string;
    disabled?: boolean;
    selected?: boolean;
    onSelect?: (value: Value) => void;
};

export type RadioGroupItemsProps<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined>
    = Partial<RadioGroupItemProps<Value, Extra>>;

export type DefaultRadioGroupItemProps<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined> = RadioGroupItemProps<Value, Extra> & Omit<LabelAsLabelProps, 'children' | 'onSelect'>;

export function DefaultRadioGroupItem<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined>({
    name,
    option,
    value,
    stringValue,
    disabled,
    selected,
    onSelect,
    className,
    ...props
}: DefaultRadioGroupItemProps<Value, Extra>) {
    const id = name ? `${name}-${stringValue}` : stringValue;

    return (
        <Label key={id} disabled={disabled} className={cn('flex items-center gap-2 cursor-pointer', className)} {...props}>
            <Radio
                id={id}
                selected={selected}
                disabled={disabled}
                onSelect={() => onSelect?.(value)}
            />
            {option.label}
        </Label>
    );
}

DefaultRadioGroupItem.displayName = 'DefaultRadioGroupItem';

export function RadioGroupItems<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined>(
    props: RadioGroupItemsProps<Value, Extra> = {},
) {
    const { disabled: propsDisabled, ...restProps } = props;
    const { options } = useOptionField<RadioGroupOption<Value, Extra>>();
    const {
        name,
        handleValueChange,
        isOptionSelected,
        valueToString,
        RenderItem,
        disabled,
    } = useRadioGroupContext<Value, Extra>();

    return (
        <>
            {options.map((option) => {
                const stringValue = valueToString.get(option.value) || String(option.value);
                const id = name ? `${name}-${stringValue}` : stringValue;
                return (
                    <RenderItem
                        key={id}
                        name={name}
                        option={option}
                        stringValue={stringValue}
                        value={option.value}
                        disabled={propsDisabled ?? disabled ?? option.disabled}
                        onSelect={handleValueChange}
                        selected={isOptionSelected(option)}
                        {...restProps}
                    />
                );
            })}
        </>
    );
}

RadioGroupItems.displayName = 'RadioGroupItems';

export type ButtonRadioGroupItemProps<Value extends PrimitiveValueType> = RadioGroupItemProps<Value> & {
    variant?: ButtonProps['variant'];
    activeVariant?: ButtonProps['variant'];
};

export function ButtonRadioGroupItem<Value extends PrimitiveValueType>({
    name,
    option,
    stringValue,
    value,
    disabled,
    selected,
    onSelect,
    variant = 'secondary',
    activeVariant = 'default',
}: ButtonRadioGroupItemProps<Value>) {
    const id = name ? `${name}-${stringValue}` : stringValue;

    return (
        <Button
            id={id}
            key={id}
            variant={selected ? activeVariant : variant}
            className="flex items-center gap-2 cursor-pointer"
            disabled={disabled}
            onClick={() => onSelect?.(value)}
        >
            {option.label}
        </Button>
    );
}

ButtonRadioGroupItem.displayName = 'ButtonRadioGroupItem';

export type StandardRadioGroupItemProps<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined> = RadioGroupItemProps<Value, Extra>;

export function StandardRadioGroupItem<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined>({
    name,
    stringValue,
    value,
    disabled,
    selected,
    onSelect,
}: StandardRadioGroupItemProps<Value, Extra>) {
    const id = name ? `${name}-${stringValue}` : stringValue;

    return (
        <Button
            id={id}
            key={id}
            variant="link"
            size="sm"
            className={cn('justify-between')}
            onClick={() => onSelect?.(value)}
            disabled={disabled}
        >
            <Radio selected={selected} />
        </Button>
    );
}

StandardRadioGroupItem.displayName = 'StandardRadioGroupItem';

export type ClassificationExtra = {
    color: string;
};

export type ClassificationRadioGroupItemProps<Value extends PrimitiveValueType, Extra extends ClassificationExtra> = RadioGroupItemProps<Value, Extra>;

export function ClassificationRadioGroupItem<Value extends PrimitiveValueType, Extra extends ClassificationExtra>({
    name,
    option,
    stringValue,
    value,
    disabled,
    selected,
    onSelect,
}: ClassificationRadioGroupItemProps<Value, Extra>) {
    const id = name ? `${name}-${stringValue}` : stringValue;

    return (
        <Button
            id={id}
            key={id}
            variant="outline"
            size="sm"
            className="w-full justify-between rounded-none first:rounded-l-md last:rounded-r-md"
            onClick={() => onSelect?.(value)}
            disabled={disabled}
            style={{
                backgroundColor: selected ? hexToRgba(option.color, 0.10) ?? 'white' : undefined,
            }}
        >
            {option.label}
            <Radio selected={selected} style={{ borderColor: hexToRgba(option.color) ?? 'white' }} />
        </Button>
    );
}

ClassificationRadioGroupItem.displayName = 'ClassificationRadioGroupItem';

export function StandardHorizontalRadioGroupItem<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined>({
    name,
    option,
    stringValue,
    value,
    disabled,
    selected,
    onSelect,
    ...props
}: RadioGroupItemProps<Value, Extra>) {
    const id = name ? `${name}-${stringValue}` : stringValue;

    return (
        <Button
            id={id}
            key={id}
            variant="outline"
            size="sm"
            className={cn('w-full justify-between rounded-none first:rounded-l-md last:rounded-r-md', selected ? 'bg-background-popover hover:bg-background-primary-hover' : 'hover:bg-background-primary border-border-primary')}
            onClick={() => onSelect?.(value)}
            disabled={disabled}
            {...props}
        >
            {option.label}
            <Radio selected={selected} />
        </Button>
    );
}

StandardHorizontalRadioGroupItem.displayName = 'StandardHorizontalRadioGroupItem';

export function StandardNoRadioRadioGroupItem<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined>({
    name,
    option,
    stringValue,
    value,
    disabled,
    selected,
    onSelect,
    ...props
}: RadioGroupItemProps<Value, Extra>) {
    const id = name ? `${name}-${stringValue}` : stringValue;

    return (
        <Button
            id={id}
            key={id}
            variant="outline"
            size="sm"
            className={cn('w-full justify-between rounded-none first:rounded-l-md last:rounded-r-md', selected ? 'bg-background-popover hover:bg-background-primary-hover font-semibold' : 'hover:bg-background-primary border-border-primary')}
            onClick={() => onSelect?.(value)}
            disabled={disabled}
            {...props}
        >
            {option.label}
        </Button>
    );
}

StandardNoRadioRadioGroupItem.displayName = 'StandardNoRadioRadioGroupItem';

export type SingleSelectRadioGroupItemProps<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined> = RadioGroupItemProps<Value, Extra> & Omit<ButtonProps, 'onClick' | 'onSelect' | 'value' | 'id' | 'disabled'>;

export function SingleSelectRadioGroupItem<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined>({
    name,
    option,
    stringValue,
    value,
    disabled,
    selected,
    onSelect,
    ...props
}: SingleSelectRadioGroupItemProps<Value, Extra>) {
    const id = name ? `${name}-${stringValue}` : stringValue;

    return (
        <Button
            id={id}
            key={id}
            variant="outline"
            size="sm"
            className={cn('h-10 w-full justify-between rounded-md border-0 hover:bg-current', selected ? 'bg-background-popover' : ''/* no additional styles when not selected */)}
            onClick={() => onSelect?.(value)}
            disabled={disabled}
            {...props}
        >
            {option.label}
            {selected && <CheckIcon />}
        </Button>
    );
}

SingleSelectRadioGroupItem.displayName = 'SingleSelectRadioGroupItem';

export type ClassificationGroupRadioGroupItemProps<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined> = RadioGroupItemProps<Value, Extra>;

export function ClassificationGroupRadioGroupItem<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined>({
    name,
    option,
    stringValue,
    value,
    disabled,
    selected,
    onSelect,
}: ClassificationGroupRadioGroupItemProps<Value, Extra>) {
    const id = name ? `${name}-${stringValue}` : stringValue;

    return (
        <Button
            id={id}
            key={id}
            variant="outline"
            size="sm"
            className={cn('w-full rounded-md text-center', selected ? 'text-brand-primary hover:bg-background-primary border-border-primary' : 'text-text-tertiary bg-background-popover hover:bg-background-primary-hover border-0')}
            onClick={() => onSelect?.(value)}
            disabled={disabled}
        >
            {option.label}
        </Button>
    );
}

ClassificationGroupRadioGroupItem.displayName = 'ClassificationGroupRadioGroupItem';

export type RadioGroupProps<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined, RenderItem extends RadioGroupRenderItem<Value, Extra> = typeof DefaultRadioGroupItem>
    = | (PrimativeFieldProps<Value> & {
        asChild: true;
        options: RadioGroupOption<Value, Extra>[];
        orientation?: 'horizontal' | 'vertical';
        renderItem?: RenderItem;
        className?: string;
        children: ReactNode;
    })
    | (PrimativeFieldProps<Value> & {
        asChild?: false;
        options: RadioGroupOption<Value, Extra>[];
        orientation?: 'horizontal' | 'vertical';
        renderItem?: RenderItem;
    } & Omit<PrimitiveRadioGroupProps, PrimativeFormPropsOmit>);

export function RadioGroup<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined, RenderItem extends RadioGroupRenderItem<Value, Extra> = typeof DefaultRadioGroupItem>(
    props: PrimativeFieldProps<Value> & {
        asChild: true;
        options: RadioGroupOption<Value, Extra>[];
        orientation?: 'horizontal' | 'vertical';
        renderItem?: RenderItem;
        className?: string;
        children: ReactNode;
    },
): ReactElement;
export function RadioGroup<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined, RenderItem extends RadioGroupRenderItem<Value, Extra> = typeof DefaultRadioGroupItem>(
    props: PrimativeFieldProps<Value> & {
        asChild?: false;
        options: RadioGroupOption<Value, Extra>[];
        orientation?: 'horizontal' | 'vertical';
        renderItem?: RenderItem;
        className?: string;
        children?: ReactNode;
    } & Omit<PrimitiveRadioGroupProps, PrimativeFormPropsOmit | 'children' | 'className'>,
): ReactElement;
export function RadioGroup<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined, RenderItem extends RadioGroupRenderItem<Value, Extra> = typeof DefaultRadioGroupItem>(props: RadioGroupProps<Value, Extra, RenderItem>) {
    const {
        asChild = false,
        name,
        value,
        onChange,
        onBlur,
        options,
        className,
        disabled,
        orientation = 'vertical',
        renderItem: RenderItem = DefaultRadioGroupItem as unknown as RenderItem,
        children,
        ...restProps
    } = props;

    const Component = asChild ? Slot : PrimitiveRadioGroup;

    const [selectedValue, setSelectedValue] = useState<Value | undefined>(value);

    const { valueToString, stringToValue } = useMemo(() => createValueMapsFromOptions<Value, RadioGroupOption<Value, Extra>>(options), [options]);

    const contextValue = useMemo<OptionFieldContextState<RadioGroupOption<Value, Extra>>>(() => ({
        options,
    }), [options]);

    useEffect(() => {
        setSelectedValue(value);
    }, [value]);

    const handleValueChange = useCallback((newValue: Value) => {
        setSelectedValue(newValue);
        onChange(newValue);
    }, [onChange]);

    const isOptionSelected = useCallback((option: Option<Value, Extra>) => {
        return selectedValue === option.value;
    }, [selectedValue]);

    const radioGroupContextValue = useMemo<RadioGroupContextState<Value, Extra, RenderItem>>(() => ({
        name,
        value,
        onChange,
        onBlur,
        disabled,
        selectedValue,
        handleValueChange,
        isOptionSelected,
        valueToString,
        RenderItem,
    }), [name, value, onChange, onBlur, disabled, selectedValue, handleValueChange, isOptionSelected, valueToString, RenderItem]);

    const defaultChildren = useMemo(() => {
        if (children !== undefined) {
            return children;
        }
        return <RadioGroupItems <Value, Extra> />;
    }, [children]);

    if (asChild) {
        return (
            <OptionFieldContext value={contextValue}>
                <RadioGroupContext value={radioGroupContextValue as unknown as RadioGroupContextState<PrimitiveValueType, OptionExtra | undefined>}>
                    <Component
                        className={cn(
                            'flex gap-2',
                            orientation === 'horizontal' ? 'flex-row' : 'flex-col',
                            className,
                        )}
                        {...restProps}
                        children={defaultChildren}
                    />
                </RadioGroupContext>
            </OptionFieldContext>
        );
    }

    return (
        <OptionFieldContext value={contextValue}>
            <RadioGroupContext value={radioGroupContextValue as unknown as RadioGroupContextState<PrimitiveValueType, OptionExtra | undefined>}>
                <Component
                    className={cn(
                        'flex gap-2',
                        orientation === 'horizontal' ? 'flex-row' : 'flex-col',
                        className,
                    )}
                    name={name}
                    value={selectedValue ? (valueToString.get(selectedValue) || String(selectedValue)) : VALUE_MARKER.UNDEFINED}
                    onValueChange={(val) => {
                        const newValue = stringToValue.get(val) as Value;
                        handleValueChange(newValue);
                    }}
                    onBlur={onBlur}
                    disabled={disabled}
                    aria-disabled={disabled}
                    data-disabled={disabled}
                    {...restProps}
                >
                    {defaultChildren}
                </Component>
            </RadioGroupContext>
        </OptionFieldContext>
    );
}

RadioGroup.displayName = 'RadioGroup';
RadioGroup.Item = DefaultRadioGroupItem;
RadioGroup.ButtonItem = ButtonRadioGroupItem;
RadioGroup.Items = RadioGroupItems;

export type RadioGroupTriggerProps = Omit<ComponentPropsWithRef<typeof Button>, 'children'> & {
    placeholder?: PrimativeLabelType;
};

export function RadioGroupTrigger<Value extends PrimitiveValueType = PrimitiveValueType>(props: RadioGroupTriggerProps) {
    const { placeholder = 'Select option', ...restProps } = props;
    const { options } = useOptionField<RadioGroupOption<Value>>();
    const { value } = useRadioGroupContext<Value>();

    const selectedOption = options.find(option => option.value === value);

    return (
        <Button variant="input" {...restProps}>
            {selectedOption?.label || placeholder}
            <ChevronDown />
        </Button>
    );
}

RadioGroup.Trigger = RadioGroupTrigger;
RadioGroupTrigger.displayName = 'RadioGroupTrigger';
